import React, { useState, useEffect } from 'react';
import {
    buildVersionedUrl,
    getMainVersions,
    getBetaVersions,
    getRCVersions,
    getVersionWithoutBuildNumber
} from '../../helper/helper';
import { APP_VERSION } from '../../variables/staticValue';
import LocalStorage from '../../config/LocalStorage';

let pendingVersionCheck = null;

const fetchAllVersions = async () => {
    if (pendingVersionCheck) {
        return pendingVersionCheck;
    }

    pendingVersionCheck = Promise.all([
        getMainVersions(),
        getBetaVersions(),
        getRCVersions()
    ]).then(([mainVersions, betaVersions, rcVersions]) => {
        return [...mainVersions, ...betaVersions, ...rcVersions];
    }).finally(() => {
        pendingVersionCheck = null;
    });

    return pendingVersionCheck;
};

const isVersionInList = (version, allVersions) => {
    const cleanVersion = getVersionWithoutBuildNumber(version);
    if (allVersions.some(v => getVersionWithoutBuildNumber(v) === cleanVersion)) return true;

    const versionBase = cleanVersion.split('-')[0];
    return allVersions.some(v => {
        const vBase = getVersionWithoutBuildNumber(v).split('-')[0];
        if (vBase === versionBase) {
            if (version.includes('-beta') && v.includes('-beta')) return true;
            if (version.includes('-rc') && v.includes('-rc')) return true;
            if (!version.includes('-') && !v.includes('-')) return true;
        }
        return false;
    });
};

const checkVersionExists = async (version) => {
    try {
        const allVersions = await fetchAllVersions();
        return isVersionInList(version, allVersions);
    } catch (error) {
        console.warn("Failed to check version existence:", error);
        return false;
    }
};

const getLatestAvailableVersion = async () => {
    try {
        const allVersions = await fetchAllVersions();
        
        const mainVersions = allVersions.filter(v => !v.includes('-'));
        if (mainVersions.length > 0) {
            return getVersionWithoutBuildNumber(mainVersions[0]);
        }

        const betaVersions = allVersions.filter(v => v.includes('-beta'));
        if (betaVersions.length > 0) {
            return getVersionWithoutBuildNumber(betaVersions[0]);
        }

        const rcVersions = allVersions.filter(v => v.includes('-rc'));
        if (rcVersions.length > 0) {
            return getVersionWithoutBuildNumber(rcVersions[0]);
        }

        return allVersions[0] ? getVersionWithoutBuildNumber(allVersions[0]) : null;
    } catch {
        return null;
    }
};

const MultiVersionWrapper = ({ children }) => {
    const [isRedirecting, setIsRedirecting] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const handleVersionRedirect = async () => {
            const pathname = window.location.pathname;
            const isRootPath = pathname === '' || pathname === '/';
            const currentVersion = getVersionWithoutBuildNumber(APP_VERSION);

            if (isRootPath) {
                const rawLastUsedVersion = LocalStorage.getLastUsedVersion();
                const lastUsedVersion = rawLastUsedVersion ? getVersionWithoutBuildNumber(rawLastUsedVersion) : null;

                if (lastUsedVersion) {
                    const isVersionValid = await checkVersionExists(lastUsedVersion);
                    if (cancelled) return;
                    if (isVersionValid) {
                        window.location.href = buildVersionedUrl(lastUsedVersion, { path: '/' });
                        return;
                    }
                }

                LocalStorage.setLastUsedVersion(currentVersion);
                window.location.href = buildVersionedUrl(currentVersion, { path: '/' });
                return;
            } else {
                const versionFromPath = pathname.split('/')[1];
                if (versionFromPath && /^\d+\.\d+\.\d+(-[a-zA-Z0-9.]+)?/.test(versionFromPath)) {
                    const isVersionValid = await checkVersionExists(versionFromPath);
                    if (cancelled) return;

                    if (isVersionValid) {
                        LocalStorage.setLastUsedVersion(versionFromPath);
                    } else {
                        console.warn(`Version ${versionFromPath} does not exist, redirecting to latest version...`);

                        const latestVersion = await getLatestAvailableVersion();
                        if (cancelled) return;
                        if (latestVersion) {
                            LocalStorage.setLastUsedVersion(latestVersion);
                            window.location.href = buildVersionedUrl(latestVersion);
                            return;
                        }

                        console.error('No available version found on server');
                    }
                }
            }

            if (!cancelled) {
                setIsRedirecting(false);
            }
        };

        handleVersionRedirect();

        return () => {
            cancelled = true;
        };
    }, []);

    if (isRedirecting) {
        return null;
    }

    return children;
};

export default MultiVersionWrapper;
